'use client'

import { useId, useState } from 'react'

import { tw } from '@/lib/utils'

import { StarIcon } from './star-icon'

export const STAR_ACTIVE_COLOR = '#4A7FE5'
// Navy at 14%: an empty star still reads as a star on the cream page and on
// a white card. At the earlier 6% the empty stars all but vanished on cream,
// so the rating input barely looked interactive.
export const STAR_INACTIVE_COLOR = 'rgba(20, 37, 70, 0.14)'

const roundScoreToStars = (score: number) => {
  const s = Number(score.toFixed(1))
  const f = Math.floor(s)
  const remainder = s - f
  if (remainder < 0.25) {
    return f
  }
  if (remainder > 0.76) {
    return Math.min(f + 1, 5)
  }
  return f + 0.5
}

interface StarRatingDisplayProps {
  score: number
  size?: number
  className?: string
  activeColor?: string
  inactiveColor?: string
  /** Accessible text for the whole rating, e.g. "3.5 out of 5 stars". Without
   *  it the stars are decorative and hidden from assistive technology. */
  label?: string
}

export const StarRatingDisplay = ({
  score,
  size = 20,
  className,
  activeColor = STAR_ACTIVE_COLOR,
  inactiveColor = STAR_INACTIVE_COLOR,
  label,
}: StarRatingDisplayProps) => {
  const stars = roundScoreToStars(score)

  return (
    <div
      className={tw('flex gap-[6px]', className)}
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
    >
      {Array.from({ length: 5 }, (_, i) => {
        const isHalf = i + 1 - stars === 0.5
        if (isHalf) {
          return (
            <div className="relative" key={i}>
              <StarIcon fill={inactiveColor} size={size} />
              <StarIcon
                fill={activeColor}
                size={size}
                className="absolute top-0 left-0"
                style={{ clipPath: 'inset(0 50% 0 0)' }}
              />
            </div>
          )
        }
        return <StarIcon key={i} fill={i + 1 <= stars ? activeColor : inactiveColor} size={size} />
      })}
    </div>
  )
}

interface StarRatingInputProps {
  value: number
  onChange: (score: number) => void
  /** Star glyph size. Each star sits in a 44px hit area regardless. */
  size?: number
  className?: string
  /** Label for the whole group, e.g. "Rate this product". */
  label: string
  /** Label for one star option given its count, e.g. "4 of 5 stars". */
  optionLabel: (stars: number) => string
}

/** Five native radios drawn as stars: Tab reaches the group, the arrow keys
 *  move the choice, and every star announces its own label. Hovering still
 *  previews a rating with the mouse. */
export const StarRatingInput = ({
  value,
  onChange,
  size = 36,
  className,
  label,
  optionLabel,
}: StarRatingInputProps) => {
  const name = useId()
  const [hoverStars, setHoverStars] = useState(0)
  const shownStars = hoverStars || value

  return (
    <div role="radiogroup" aria-label={label} className={tw('flex', className)} onMouseLeave={() => setHoverStars(0)}>
      {Array.from({ length: 5 }, (_, i) => {
        const stars = i + 1
        return (
          <label
            key={stars}
            className="flex h-[44px] w-[44px] cursor-pointer items-center justify-center"
            onMouseEnter={() => setHoverStars(stars)}
          >
            <input
              type="radio"
              name={name}
              value={stars}
              checked={value === stars}
              onChange={() => onChange(stars)}
              aria-label={optionLabel(stars)}
              data-tracking="ignore"
              className="peer sr-only"
            />
            <StarIcon
              fill={stars <= shownStars ? STAR_ACTIVE_COLOR : STAR_INACTIVE_COLOR}
              size={size}
              className="rounded-sm peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-lapis"
            />
          </label>
        )
      })}
    </div>
  )
}
